import { ClientError, SensitiveActionCancelledError } from '@repo/api-kit/queries/utils';
import { getHttpStatus } from './get-http-status';
import { isLazyLoadingError } from './is-lazy-loading-error';
import { isNetworkError } from './is-network-error';

export interface ShouldSendToSentryOptions {
  /**
   * HTTP status codes the caller wants filtered out, on top of the defaults
   * (5xx, 401, abort, network errors). Useful when the Qonto backend uses a
   * status semantically — e.g. 405 is returned as a permission/role gate at
   * several endpoints rather than a true verb mismatch.
   */
  ignoreStatuses?: readonly number[];
}

// The GraphQL gateway already reports these in its own Sentry project, filtering because there's no action from FE
function isGraphQLServiceUnavailable(error: unknown): boolean {
  if (!(error instanceof ClientError)) return false;
  const errors: readonly { extensions?: { code?: unknown } }[] = error.response.errors ?? [];
  return errors.length > 0 && errors.every(e => e.extensions?.code === 'service_unavailable');
}

export function shouldSendToSentry(error: unknown, options?: ShouldSendToSentryOptions): boolean {
  if (isLazyLoadingError(error) || isGraphQLServiceUnavailable(error)) {
    return false;
  }

  // Don't send 5xx, 401, caller-ignored statuses, abort errors, or network errors to Sentry
  if (error && typeof error === 'object') {
    if ('name' in error && error.name === 'CancelledError') {
      return false;
    }

    // User dismissed the SCA prompt — expected control flow, not a failure.
    if (error instanceof SensitiveActionCancelledError) {
      return false;
    }
    const status = getHttpStatus(error);

    // Caller-specified statuses to ignore
    if (typeof status === 'number' && options?.ignoreStatuses?.includes(status)) {
      return false;
    }

    // Check for 5xx errors
    if (status && status >= 500 && status < 600) {
      return false;
    }

    // Check for 401 errors
    if (status === 401) {
      return false;
    }

    // Check for 426 errors
    if (status === 426) {
      return false;
    }

    // Check for abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }

    // Check for network errors
    if (isNetworkError(error)) {
      return false;
    }
  }

  return true;
}
